/* eslint-disable restrict-imports/restrict-imports */
import React, { useState, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { FormProps, ISubmitEvent, Field, Widget } from '@rjsf/core';
import Form from '@rjsf/material-ui';
import { Box, Typography } from '@material-ui/core';
import { Button } from 'design-system/Button';
import ObjectIdInput from 'react-scale/components/design-system/ObjectIdInput';
import StringInput from 'react-scale/components/design-system/StringInput';
import { Intent } from 'react-scale/components/design-system/theme';
import { makeStyles } from 'react-scale/components/design-system';

// imports for customized components
import SelectType from 'corp/components/UI/SelectType';
import SelectProject from 'react-scale/corp/components/UI/SelectProject';

export type CorpFormSubmitHandler<T = any> = (
  submitEvent: ISubmitEvent<T>,
) => Promise<JSX.Element | string | void | undefined>;
interface CorpFormProps<T = object> extends FormProps<T> {
  submitLabel?: string;
  /**
   * Handler for submission; takes in ({ formData }), where formData is a json that conforms to the json schema provided.
   *
   * onSubmit should return a promise; if that promise resolves to a string, it will be displayed after below the submit button
   */
  onSubmit: CorpFormSubmitHandler;
  onCancel?: () => void;
  type?: 'primary' | 'tertiary' | Intent;
}

/**
 * A light-weight wrapper around react-jsonschema-form that supports generation of form components
 *
 * See https://rjsf-team.github.io/react-jsonschema-form/ for a demo
 */
export const CorpForm: React.FC<CorpFormProps> = ({
  submitLabel = 'Submit',
  onSubmit,
  onCancel,
  ...props
}) => {
  const classes = useStyles();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [message, setMessage] = useState<string | JSX.Element>();
  const handleSubmit = useCallback(
    async (submitEvent: any) => {
      setSubmitting(true);
      try {
        const result = await onSubmit(submitEvent);
        setMessage(result ?? 'Success');
      } catch (e) {
        setMessage(e.response?.message || 'Failed');
      }
      setSubmitting(false);
    },
    [onSubmit],
  );
  return (
    <Form
      {...props}
      className={props.className ?? classes.container}
      disabled={props.disabled || !onSubmit || submitting}
      onSubmit={handleSubmit}
      fields={customFields}
      widgets={customWidgets}
    >
      <Box marginTop={3}>
        {onCancel && (
          <Button type="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}{' '}
        <Button
          htmlType="submit"
          disabled={props.disabled || !onSubmit || submitting}
          type={props.type ?? 'primary'}
        >
          {submitLabel}
        </Button>
        {message}
      </Box>
    </Form>
  );
};

const useStyles = makeStyles({
  container: {
    marginTop: 24,
    marginBottom: 24,
    '& .MuiFormControl-root.MuiFormControl-fullWidth, .MuiFormControl-root.MuiFormControl-fullWidth > .MuiPaper-elevation2 > .MuiBox-root':
      {
        '& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)': {
          // fix bug w/ nested object in array
          top: 'unset',
          bottom: 8,
          transform: 'translate(0, 0) scale(1)',
        },
        '& > .MuiBox-root:first-child': {
          '& > .MuiDivider-root': {
            display: 'none',
            marginBottom: 0,
          },
        },
        '& .MuiGrid-item': {
          paddingTop: 0,
          paddingBottom: 0,
        },
        '& .MuiButton-root': {
          textTransform: 'unset',
        },
        '& .MuiGrid-container > .MuiGrid-root.MuiGrid-container.MuiGrid-justify-xs-flex-end': {
          justifyContent: 'unset', // "add item" button should float left
        },
        '& > :not(.MuiBox-root:first-child)': {
          '& .MuiTypography-h5': {
            fontWeight: 'unset',
            // color: 'rgba(0, 0, 0, 0.80)',
            fontSize: '0.7rem',
            marginBottom: 0,
          },
          '& .MuiTypography-subtitle2': {
            fontWeight: 'unset',
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '0.8rem',
          },
        },
        '& .MuiTypography-h5': {
          fontSize: '0.9rem',
        },
      },
  },
});
// ============= Custom Fields and Widgets ============
// https://react-jsonschema-form.readthedocs.io/en/docs/advanced-customization/custom-widgets-fields/#adding-your-own-custom-widgets

const ObjectIdsField: Field = (props: any) => {
  const [key, setKey] = useState<number>(0); // need to reset ObjectIdInput component after a submission by incrementing its key
  const isEmpty = _.isEmpty(props.formData);
  useEffect(() => {
    if (isEmpty) setKey(k => k + 1);
  }, [isEmpty]);
  return (
    <div>
      <ObjectIdInput key={key} hideLabelPrompt onValidEntry={props.onChange} />
      {props.schema.description && (
        <span style={{ marginLeft: 8 }}>
          <Typography variant="caption" color="textSecondary">
            {props.schema.description}
          </Typography>
        </span>
      )}
    </div>
  );
};

const StringsField: Field = (props: any) => {
  const [key, setKey] = useState<number>(0); // need to reset StringInput component after a submission by incrementing its key
  const isEmpty = _.isEmpty(props.formData);
  useEffect(() => {
    if (isEmpty) setKey(k => k + 1);
  }, [isEmpty]);
  const initialInput = Array.isArray(props.schema.default)
    ? props.schema.default.join(', ')
    : typeof props.schema.default === 'string'
    ? props.schema.default
    : undefined;

  return (
    <div>
      <StringInput
        key={key}
        hideLabelPrompt
        onValidEntry={props.onChange}
        initialInput={initialInput}
      />
      {props.schema.description && (
        <span style={{ marginLeft: 8 }}>
          <Typography variant="caption" color="textSecondary">
            {props.schema.description}
          </Typography>
        </span>
      )}
    </div>
  );
};

const SelectProjectWidget: Widget = (props: any) => {
  const [project, setProject] = useState<any>();

  // clear upon submission
  useEffect(() => {
    if (props.value === undefined) setProject(undefined);
  }, [props.value]);

  const handleChange = (input: { value: string; label: string }) => {
    setProject(input);
    props.onChange(input?.value);
  };

  return (
    <div>
      <SelectProject value={project} onChange={handleChange} />
    </div>
  );
};

const SelectTypeWidget: Widget = (props: any) => {
  const [selection, setSelection] = useState<any>();

  // clear upon submission
  useEffect(() => {
    if (props.value === undefined) setSelection(undefined);
  }, [props.value]);

  const handleChange = (input: any) => {
    setSelection(input);
    props.onChange(input?.value);
  };

  return (
    <div>
      <SelectType
        type={(props.options.selectType as string) || ''}
        placeholder={props.placeholder || 'Select a type'}
        value={selection}
        onChange={handleChange}
      />
    </div>
  );
};

const customWidgets = {
  SelectProjectWidget,
  SelectTypeWidget,
};

const customFields = {
  ObjectIdsField,
  StringsField,
};
